import { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Stack,
  Grid,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SchoolIcon from '@mui/icons-material/School';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import CountUp from 'react-countup';
import { useSpring, animated } from 'react-spring';
import ConsultationForm from './ConsultationForm';

const features = [
  {
    icon: <TrendingUpIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Self-Hosted Solution',
    description: 'Complete control over your email infrastructure with our self-hosted MailWizz deployment and management services.'
  },
  {
    icon: <EmailIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Campaign Management',
    description: 'Expert setup and optimization of MailWizz campaigns for maximum deliverability and engagement.'
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Security & Compliance',
    description: 'Implementation of robust security measures and compliance protocols for your MailWizz instance.'
  },
  {
    icon: <GroupIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: '20+ Deployments',
    description: 'Proven track record of successful MailWizz deployments and management for various businesses.'
  },
  {
    icon: <ShoppingCartIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'High-Volume Sending',
    description: 'Optimized infrastructure for handling large-scale email campaigns with maximum deliverability.'
  },
  {
    icon: <AnalyticsIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Advanced Analytics',
    description: 'Comprehensive reporting and analytics tools to track campaign performance and optimize results.'
  }
];

const serviceCategories = [
  {
    title: 'Setup & Deployment',
    icon: <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'MailWizz Installation',
      'Server Configuration',
      'Domain Setup',
      'SSL Implementation'
    ]
  },
  {
    title: 'Campaign Management',
    icon: <IntegrationInstructionsIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Campaign Strategy',
      'Template Design',
      'List Management',
      'A/B Testing'
    ]
  },
  {
    title: 'Performance Optimization',
    icon: <AutoGraphIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Server Tuning',
      'Database Optimization',
      'Queue Management',
      'Load Balancing'
    ]
  },
  {
    title: 'Security & Compliance',
    icon: <AnalyticsIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Security Hardening',
      'GDPR Compliance',
      'Anti-Spam Measures',
      'Access Control'
    ]
  },
  {
    title: 'API Integration',
    icon: <MarkEmailReadIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Custom API Development',
      'Third-party Integration',
      'Webhook Setup',
      'Data Synchronization'
    ]
  },
  {
    title: 'Support & Maintenance',
    icon: <SchoolIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      '24/7 Monitoring',
      'Regular Updates',
      'Backup Management',
      'Technical Support'
    ]
  }
];

const heroMetrics = [
  { label: 'Deployments', value: 20, suffix: '+', color: '#0B60B1' },
  { label: 'Success Rate', value: 95, suffix: '%', color: '#4CAF50' },
  { label: 'Client Satisfaction', value: 98, suffix: '%', color: '#FF9800' }
];

const animatedData = [
  { month: 'Jan', emails: 30000, opens: 12000, clicks: 6000 },
  { month: 'Feb', emails: 35000, opens: 14000, clicks: 7000 },
  { month: 'Mar', emails: 40000, opens: 16000, clicks: 8000 },
  { month: 'Apr', emails: 45000, opens: 18000, clicks: 9000 },
  { month: 'May', emails: 50000, opens: 20000, clicks: 10000 },
  { month: 'Jun', emails: 55000, opens: 22000, clicks: 11000 }
];

export default function MailWizzPage() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const fadeIn = useSpring({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
    config: { duration: 1000 }
  });

  return (
    <Box sx={{ bgcolor: '#f7fafd', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box sx={{ bgcolor: 'primary.main', color: '#fff', py: { xs: 6, md: 10 }, position: 'relative' }}>
        <Container maxWidth="lg">
          <animated.div style={fadeIn}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h3" fontWeight={700} gutterBottom>
                MailWizz Consulting
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, fontWeight: 400 }}>
                Expert deployment, configuration, and optimization of your self-hosted MailWizz email marketing platform
              </Typography>
            </Box>

            {/* Animated Metrics */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 6 }}>
              {heroMetrics.map((metric, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                    minWidth: 200,
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" fontWeight={700} sx={{ color: 'white' }}>
                    <CountUp end={metric.value} suffix={metric.suffix} duration={2.5} />
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: 'white' }}>
                    {metric.label}
                  </Typography>
                </Paper>
              ))}
            </Box>

            {/* Animated Chart */}
            <Box sx={{ height: 300, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2, p: 3 }}>
              <ResponsiveContainer>
                <LineChart data={animatedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis dataKey="month" stroke="#fff" />
                  <YAxis yAxisId="left" stroke="#fff" />
                  <YAxis yAxisId="right" orientation="right" stroke="#fff" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      border: 'none',
                      borderRadius: 4
                    }}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="emails"
                    stroke="#0B60B1"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="opens"
                    stroke="#4CAF50"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="clicks"
                    stroke="#FF9800"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </animated.div>
        </Container>
      </Box>

      {/* Features with Image Section */}
      <Box sx={{ py: 12, bgcolor: '#fff', position: 'relative', overflow: 'hidden' }}>
        {/* Background decorative elements */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '40%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(11,96,177,0.05) 0%, rgba(11,96,177,0) 100%)',
          clipPath: 'polygon(100% 0, 100% 100%, 0 100%, 100% 0)',
          zIndex: 0
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '30%',
          height: '60%',
          background: 'linear-gradient(45deg, rgba(11,96,177,0.03) 0%, rgba(11,96,177,0) 100%)',
          clipPath: 'polygon(0 0, 100% 0, 0 100%, 0 0)',
          zIndex: 0
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'stretch' }}>
            {/* Left Column - First 3 Cards */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Stack spacing={4}>
                {features.slice(0, 3).map((feature, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 3,
                      transition: 'all 0.3s ease-in-out',
                      border: '1px solid rgba(11,96,177,0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '4px',
                        height: '100%',
                        background: 'linear-gradient(to bottom, #0B60B1, #2196F3)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease-in-out'
                      },
                      '&:hover': {
                        transform: 'translateX(8px)',
                        boxShadow: '0 8px 32px rgba(11,96,177,0.1)',
                        borderColor: 'rgba(11,96,177,0.2)',
                        '&::before': {
                          opacity: 1
                        }
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 2, 
                        background: 'linear-gradient(45deg, #0B60B1 30%, #2196F3 90%)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(11,96,177,0.2)'
                      }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" sx={{ ml: 2, fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Box>

            {/* Middle Column - Image */}
            <Box sx={{ 
              flex: 1.5,
              display: 'flex', 
              alignItems: 'center',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-20px',
                right: '-20px',
                width: '200px',
                height: '200px',
                background: 'linear-gradient(45deg, #0B60B1 0%, #2196F3 100%)',
                borderRadius: '50%',
                opacity: 0.1,
                zIndex: 0
              }
            }}>
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  height: '600px',
                  borderRadius: 4,
                  overflow: 'hidden',
                  position: 'relative',
                  bgcolor: '#f7fafd',
                  boxShadow: '0 20px 40px rgba(11,96,177,0.1)',
                  transform: 'perspective(1000px) rotateY(-15deg) rotateX(5deg)',
                  transition: 'all 0.5s ease-in-out',
                  '&:hover': {
                    transform: 'perspective(1000px) rotateY(-5deg) rotateX(2deg)',
                    boxShadow: '0 30px 60px rgba(11,96,177,0.2)',
                  }
                }}
              >
                <Box
                  component="img"
                  src="/images/mailwizz.png"
                  alt="MailWizz Platform"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    transition: 'all 0.5s ease-in-out',
                    filter: 'brightness(0.95) contrast(1.1)',
                    '&:hover': {
                      filter: 'brightness(1) contrast(1.2)',
                    }
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(180deg, rgba(11,96,177,0.1) 0%, rgba(11,96,177,0) 50%, rgba(0,0,0,0.7) 100%)',
                    zIndex: 1
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 6,
                    zIndex: 2
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 3,
                    '&::before': {
                      content: '""',
                      width: '40px',
                      height: '4px',
                      background: 'linear-gradient(90deg, #0B60B1, #2196F3)',
                      borderRadius: '2px',
                      mr: 2
                    }
                  }}>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 700, 
                      color: 'white',
                      textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      MailWizz Platform
                    </Typography>
                  </Box>
                  <Typography variant="subtitle1" sx={{ 
                    color: 'white', 
                    opacity: 0.9, 
                    mb: 4,
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }}>
                    Enterprise-grade email marketing solution with advanced features
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                      variant="contained" 
                      sx={{ 
                        background: 'linear-gradient(45deg, #0B60B1 30%, #2196F3 90%)',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(11,96,177,0.3)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #0B60B1 40%, #2196F3 100%)',
                          boxShadow: '0 6px 16px rgba(11,96,177,0.4)',
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease-in-out'
                      }}
                    >
                      Learn More
                    </Button>
                    <Button 
                      variant="outlined" 
                      sx={{ 
                        borderColor: 'white',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease-in-out'
                      }}
                    >
                      View Demo
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Box>

            {/* Right Column - Last 3 Cards */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Stack spacing={4}>
                {features.slice(3, 6).map((feature, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 3,
                      transition: 'all 0.3s ease-in-out',
                      border: '1px solid rgba(11,96,177,0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '4px',
                        height: '100%',
                        background: 'linear-gradient(to bottom, #0B60B1, #2196F3)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease-in-out'
                      },
                      '&:hover': {
                        transform: 'translateX(8px)',
                        boxShadow: '0 8px 32px rgba(11,96,177,0.1)',
                        borderColor: 'rgba(11,96,177,0.2)',
                        '&::before': {
                          opacity: 1
                        }
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 2, 
                        background: 'linear-gradient(45deg, #0B60B1 30%, #2196F3 90%)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(11,96,177,0.2)'
                      }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" sx={{ ml: 2, fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Service Categories */}
      <Box sx={{ bgcolor: '#f7fafd', py: 12, position: 'relative', overflow: 'hidden' }}>
        {/* Background decorative elements */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '30%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(11,96,177,0.05) 0%, rgba(11,96,177,0) 100%)',
          clipPath: 'polygon(0 0, 100% 0, 0 100%, 0 0)',
          zIndex: 0
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '40%',
          height: '60%',
          background: 'linear-gradient(45deg, rgba(11,96,177,0.03) 0%, rgba(11,96,177,0) 100%)',
          clipPath: 'polygon(100% 0, 100% 100%, 0 100%, 100% 0)',
          zIndex: 0
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h4" 
              component="h2" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(45deg, #0B60B1 30%, #2196F3 90%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2
              }}
            >
              Our Services
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
              Comprehensive MailWizz solutions tailored to your business needs
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {serviceCategories.map((category, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    transition: 'all 0.3s ease-in-out',
                    border: '1px solid rgba(11,96,177,0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '4px',
                      height: '100%',
                      background: 'linear-gradient(to bottom, #0B60B1, #2196F3)',
                      opacity: 0,
                      transition: 'opacity 0.3s ease-in-out'
                    },
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 32px rgba(11,96,177,0.1)',
                      borderColor: 'rgba(11,96,177,0.2)',
                      '&::before': {
                        opacity: 1
                      }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: 'linear-gradient(45deg, #0B60B1 30%, #2196F3 90%)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(11,96,177,0.2)'
                    }}>
                      {category.icon}
                    </Box>
                    <Typography variant="h6" sx={{ ml: 2, fontWeight: 600 }}>
                      {category.title}
                    </Typography>
                  </Box>
                  <Stack spacing={2}>
                    {category.services.map((service, serviceIndex) => (
                      <Box 
                        key={serviceIndex} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            transform: 'translateX(8px)'
                          }
                        }}
                      >
                        <CheckCircleIcon sx={{ 
                          color: 'primary.main', 
                          mr: 1.5, 
                          fontSize: 20,
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.2)'
                          }
                        }} />
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                          {service}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Consultation Form */}
      <Container maxWidth="lg">
        <ConsultationForm
          title="Ready to Transform Your Email Marketing?"
          formName="mailwizz_consultation"
          formSource="mailwizz_page"
          bulletPoints={[
            'Set up and optimize your MailWizz instance',
            'Configure high-performance email infrastructure',
            'Implement security and compliance measures',
            'Optimize email deliverability',
            'Set up campaign tracking and analytics',
            'Train your team on best practices'
          ]}
          additionalFields={[
            {
              name: 'monthly_volume',
              label: 'Monthly Email Volume',
              type: 'select',
              required: true,
              options: [
                { value: '1-10k', label: '1,000 - 10,000 emails' },
                { value: '10-50k', label: '10,000 - 50,000 emails' },
                { value: '50-100k', label: '50,000 - 100,000 emails' },
                { value: '100-500k', label: '100,000 - 500,000 emails' },
                { value: '500k+', label: '500,000+ emails' }
              ]
            },
            {
              name: 'current_platform',
              label: 'Current Email Platform',
              type: 'select',
              required: true,
              options: [
                { value: 'none', label: 'No current platform' },
                { value: 'mailchimp', label: 'Mailchimp' },
                { value: 'sendgrid', label: 'SendGrid' },
                { value: 'mailgun', label: 'Mailgun' },
                { value: 'amazon-ses', label: 'Amazon SES' },
                { value: 'other', label: 'Other' }
              ]
            },
            {
              name: 'use_case',
              label: 'Primary Use Case',
              type: 'select',
              required: true,
              options: [
                { value: 'marketing', label: 'Marketing Campaigns' },
                { value: 'transactional', label: 'Transactional Emails' },
                { value: 'newsletter', label: 'Newsletters' },
                { value: 'automation', label: 'Email Automation' },
                { value: 'bulk', label: 'Bulk Sending' }
              ]
            }
          ]}
        />
      </Container>
    </Box>
  );
} 